import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import {
  ConfirmSchemaResponse,
  ErrorLogListResponse,
  ErrorLogQueryParams,
  SchemaValidationMessage,
  UploadListResponse,
  UploadProgressMessage,
  UploadQueryParams,
  UploadResponse,
  UploadRootProjectDetails
} from '../models/project-upload.model';
import { BaseApiService } from './base-api.service';
import { CustomKeycloakService } from './keycloak.service';

@Injectable({
  providedIn: 'root'
})
export class ProjectUploadService extends BaseApiService {
  constructor(
    http: HttpClient,
    router: Router,
    keycloakService: CustomKeycloakService
  ) {
    super(http, router, keycloakService);
  }

  uploadFile(
    projectId: string,
    file: File,
    _forceUpload = false,
    tenantId?: string,
    teamId?: string,
    folderId?: string,
    _userId?: string
  ): Observable<UploadResponse> {
    if (!file || file.size === 0) {
      throw new Error('Invalid file: file is null, undefined, or empty');
    }

    const formData = new FormData();
    const finalFolderId = folderId || projectId; // Use projectId as folderId if not provided

    formData.append('file', file);
    formData.append('folder_id', finalFolderId);

    return this.http.post<UploadResponse>(
      `${this.baseUrl}/projects/${projectId}/uploads`,
      formData
    );
  }

  getUploads(
    projectId: string,
    query?: UploadQueryParams
  ): Observable<UploadListResponse> {
    const params = new HttpParams({
      fromObject: (query as any) || ({} as any)
    });
    return this.http.get<UploadListResponse>(
      `${this.baseUrl}/projects/${projectId}/uploads`,
      {
        params
      }
    );
  }

  confirmUploadSchema(
    projectId: string,
    uploadId: string,
    body: Record<string, unknown>
  ): Observable<ConfirmSchemaResponse> {
    return this.http.post<ConfirmSchemaResponse>(
      `${this.baseUrl}/uploads/${uploadId}/confirm/`,
      body
    );
  }

  getErrorLogs(
    projectId: string,
    uploadId: string,
    _query?: ErrorLogQueryParams
  ): Observable<ErrorLogListResponse> {
    return this.http.get<ErrorLogListResponse>(
      `${this.baseUrl}/projects/${projectId}/uploads/${uploadId}/logs`
    );
  }

  getUploadDetails(
    projectId: string,
    uploadId: string
  ): Observable<UploadResponse> {
    // New API endpoint: GET /projects/{project_id}/uploads/{upload_id}
    return this.http.get<UploadResponse>(
      `${this.baseUrl}/projects/${projectId}/uploads/${uploadId}`
    );
  }

  getSchemaValidationMessage(
    projectId: string,
    uploadId: string
  ): Observable<SchemaValidationMessage> {
    return this.http.get<SchemaValidationMessage>(
      `${this.baseUrl}/uploads/${uploadId}/schema-validation-message/`
    );
  }

  deleteUpload(projectId: string, uploadId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/uploads/${uploadId}/`);
  }

  getRootProjectDetails(
    projectId: string
  ): Observable<UploadRootProjectDetails> {
    // Note: This endpoint has been removed in the new API
    // Removed console.warn for production

    // Return a mock response for backward compatibility
    return new Observable<UploadRootProjectDetails>((observer) => {
      observer.next({
        id: projectId,
        name: 'Root Project',
        description: '',
        path: '',
        is_project: true
      });
      observer.complete();
    });
  }

  listenUploadMessages(): Observable<UploadProgressMessage> {
    // Note: Upload progress messages now come through the main notification WebSocket
    // This method should be updated to use the WebSocketNotificationService
    // Removed console.warn for production

    // For now, return an empty observable
    // TODO: Implement proper integration with WebSocketNotificationService
    return new Observable<UploadProgressMessage>((observer) => {
      observer.complete();
    });
  }
}
